import React from "react";

import TextElement from "../../../components_v2/ContentElements/TextElement";
import {
  DateElement,
  DateRangeElement,
} from "@/components_v2/ContentElements/DateElement";
import {
  ChoiceOptionWithScaleLabel,
  ContentTypeRelations,
  DisplayFieldValue,
} from "@/domains/datablocks/types";
import ContentItem from "../../../components_v2/ContentElements/ContentItem";
import { useDataBlockRelatedData } from "../hooks/useDataBlockRelatedData";
import BaseSpinner from "../../../components_v2/Spiner/BaseSpinner";
import { TextType } from "@/components_v2/ContentElements/types";
import {
  getRendererComponentForContentType,
  getRendererPropsForContentType,
} from "./contentTypeConfig";
import {
  ChoiceOptionResponse,
  ContentType,
  DataFieldResponse,
  DisplayType,
  DynamicValueResponse,
  FieldType,
  SelectedOptionResponse,
} from "@/redux/datablocks/types";

const RelatedDataDisplayComponent = ({
  dataField,
  values,
  component: Component,
  transformValue,
}: {
  dataField: DataFieldResponse;
  values: number | string | number[] | (number | string)[];
  component: React.ComponentType;
  transformValue?: (data: ContentTypeRelations) => ChoiceOptionWithScaleLabel[];
}) => {
  const choices: { ids: number[]; other: string[] } = { ids: [], other: [] };
  if (Array.isArray(values)) {
    values.forEach((val) => {
      if (typeof val === "string") {
        choices.other.push(val);
      } else choices.ids.push(val);
    });
  } else {
    if (typeof values === "string") {
      choices.other.push(values);
    } else choices.ids.push(values);
  }

  const { data, isLoading } = useDataBlockRelatedData(
    choices.ids,
    dataField.contentType,
  );

  if (isLoading) return <BaseSpinner size="small" />;

  const transformedData = transformValue ? transformValue(data) : data;
  const transformedProps = getRendererPropsForContentType(dataField, [
    ...(transformedData || []),
    ...choices.other.map((i) => ({ label: i, value: i })),
  ] as ContentTypeRelations);

  if (!transformedProps) return null;
  return <Component {...transformedProps} />;
};

export const getFieldDisplay = (
  dataField: DataFieldResponse,
  value: DynamicValueResponse,
) => {
  if (value == null) {
    return {
      component: TextElement,
      props: { value: undefined },
    };
  }

  switch (dataField.type) {
    case FieldType.TEXT:
      return {
        component: TextElement,
        props: {
          value,
          format: dataField.formatType,
          multiple: dataField.multiple,
          type:
            dataField.displayType === DisplayType.RICH_TEXT
              ? TextType.RICH_TEXT
              : TextType.REGULAR,
        },
      };

    case FieldType.NUMBER:
      return {
        component: TextElement,
        props: {
          value: value as string,
        },
      };

    case FieldType.DATE: {
      if (dataField.displayType === DisplayType.DATE_RANGE_PICKER) {
        return {
          component: DateRangeElement,
          props: {
            value: value as number[],
          },
        };
      }
      return {
        component: DateElement,
        props: {
          value: value as string,
        },
      };
    }

    case FieldType.SELECT: {
      if (dataField.displayType === DisplayType.DROPDOWN_WITH_SCALE) {
        const selectedOptions = value as SelectedOptionResponse[];
        const { data: scaleData } = useDataBlockRelatedData(
          selectedOptions.map((opt) => opt.scale),
          ContentType.Options,
        ) as { data: ChoiceOptionResponse[] };

        const scaleDataMappings = scaleData
          ? selectedOptions.reduce<Record<number, string | undefined>>(
              (acc, option) => {
                const scale = scaleData.find(
                  (scaleOption) => scaleOption.id === option.scale,
                );
                acc[option.choice] = scale?.label;
                return acc;
              },
              {},
            )
          : {};
        return {
          component: RelatedDataDisplayComponent,
          props: {
            dataField,
            values: selectedOptions.map((opt) => opt.choice),
            component: getRendererComponentForContentType(dataField),
            transformValue: (options: ChoiceOptionResponse[]) =>
              (options || []).map((option) => ({
                ...option,
                scaleLabel: scaleDataMappings[option.id] ?? "",
              })),
          },
        };
      }

      let values;
      if (dataField.multiple) {
        values = value as number[];
      } else {
        values = value as number;
      }

      return {
        component: RelatedDataDisplayComponent,
        props: {
          dataField,
          values,
          component: getRendererComponentForContentType(dataField),
        },
      };
    }

    default:
      return {
        component: TextElement,
        props: {
          value: String(value),
        },
      };
  }
};

export function getDisplayComponentForField(
  dataField: DataFieldResponse,
  value: DynamicValueResponse,
) {
  if (value == null) {
    return {
      component: TextElement,
      props: { value: undefined },
    };
  }

  const display = getFieldDisplay(dataField, value);

  if (!display) {
    throw new Error(`Can't find component for field of type ${dataField.type}`);
  }

  return display;
}

/**
 * Retrieves the display component according to the field type and returns it inside a Content Item component with a label and properties.
 */
export const getContentItemForField = (
  fieldValue: DisplayFieldValue,
  additionalProps: Record<string, string | undefined> = {},
) => {
  const { component: FieldComponent, props: fieldProps } =
    getDisplayComponentForField(fieldValue.field, fieldValue.value);
  const isCheckbox = fieldValue.field.displayType === DisplayType.CHECKBOX;
  if (isCheckbox && fieldValue.value == null) return;

  const content =
    fieldValue.value == undefined ||
    (Array.isArray(fieldValue.value) && fieldValue.value.length === 0) ? (
      <p>—</p>
    ) : (
      // @ts-expect-error props for the component
      <FieldComponent {...fieldProps} />
    );

  return (
    <ContentItem
      key={fieldValue.field.id}
      label={fieldValue.field.title}
      content={content}
      {...additionalProps}
    />
  );
};
